import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { STATUS_STYLES } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { ClipboardList, CheckCircle2, Percent, Gauge, ListChecks, AlarmClock, TrendingUp, Repeat, Sparkles, ArrowRight, SlidersHorizontal } from "lucide-react";
import { AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { DeptMatrix } from "../components/DeptMatrix";

function Kpi({ icon: Icon, label, value, accent, testid }) {
  return (
    <div data-testid={testid} className="bg-white border border-[#E7E5DF] rounded-xl p-5 hover:shadow-md transition-shadow">
      <div className="flex items-center justify-between">
        <p className="text-xs font-medium uppercase tracking-wider text-stone-500">{label}</p>
        <Icon className={`w-5 h-5 ${accent || "text-[#14532D]"}`} strokeWidth={1.5} />
      </div>
      <p className="mt-3 text-3xl font-extrabold tracking-tight text-stone-900" style={{ fontFamily: "Manrope" }}>{value}</p>
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [ai, setAi] = useState(null);
  const [hotels, setHotels] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [regions, setRegions] = useState([]);
  const [filters, setFilters] = useState({ template_id: "all", audit_type: "all", region: "all", hotel_id: "all" });
  const isCorp = ["corporate_admin", "regional_head"].includes(user.role);

  useEffect(() => {
    api.get("/hotels").then((r) => setHotels(r.data));
    api.get("/templates").then((r) => setTemplates(r.data));
    api.get("/meta").then((r) => setRegions((r.data.regions || []).filter(Boolean)));
    if (isCorp) api.get("/ai/insights/latest").then((r) => setAi(r.data)).catch(() => {});
  }, [isCorp]);

  useEffect(() => {
    const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => v !== "all"));
    api.get("/dashboard", { params }).then((r) => setData(r.data)).catch(() => {});
  }, [filters]);

  if (!data)
    return <div className="flex justify-center py-24"><div className="w-8 h-8 border-2 border-[#14532D] border-t-transparent rounded-full animate-spin" /></div>;

  const k = data.kpis;
  return (
    <div className="max-w-7xl mx-auto space-y-8" data-testid="dashboard-page">
      <div>
        <h1 className="text-3xl font-extrabold tracking-tight text-stone-900">Welcome back, {user.name?.split(" ")[0]}</h1>
        <p className="text-stone-500 mt-1 text-sm">Portfolio compliance at a glance.</p>
      </div>

      <div data-testid="dashboard-filters" className="flex flex-wrap items-center gap-3 bg-white border border-[#E7E5DF] rounded-xl px-4 py-3">
        <span className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-stone-500">
          <SlidersHorizontal className="w-3.5 h-3.5" />Filters
        </span>
        <Select value={filters.template_id} onValueChange={(v) => setFilters({ ...filters, template_id: v })}>
          <SelectTrigger className="w-52 h-9 text-sm" data-testid="filter-template"><SelectValue placeholder="Audit" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All audits</SelectItem>
            {templates.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filters.audit_type} onValueChange={(v) => setFilters({ ...filters, audit_type: v })}>
          <SelectTrigger className="w-36 h-9 text-sm" data-testid="filter-audit-type"><SelectValue placeholder="Type" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All types</SelectItem>
            <SelectItem value="internal">Internal</SelectItem>
            <SelectItem value="external">External</SelectItem>
            <SelectItem value="self">Self</SelectItem>
          </SelectContent>
        </Select>
        {isCorp && (
          <Select value={filters.region} onValueChange={(v) => setFilters({ ...filters, region: v })}>
            <SelectTrigger className="w-40 h-9 text-sm" data-testid="filter-region"><SelectValue placeholder="Region" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All regions</SelectItem>
              {regions.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
        <Select value={filters.hotel_id} onValueChange={(v) => setFilters({ ...filters, hotel_id: v })}>
          <SelectTrigger className="w-52 h-9 text-sm" data-testid="filter-hotel"><SelectValue placeholder="Hotel" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All hotels</SelectItem>
            {hotels.map((h) => <SelectItem key={h.id} value={h.id}>{h.name}</SelectItem>)}
          </SelectContent>
        </Select>
        {Object.values(filters).some((v) => v !== "all") && (
          <button data-testid="filter-clear" className="text-xs font-semibold text-[#14532D] hover:underline" onClick={() => setFilters({ template_id: "all", audit_type: "all", region: "all", hotel_id: "all" })}>Clear</button>
        )}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 stagger">
        <Kpi testid="kpi-total-audits" icon={ClipboardList} label="Total Audits" value={k.total_audits} />
        <Kpi testid="kpi-completed" icon={CheckCircle2} label="Completed" value={k.completed_audits} />
        <Kpi testid="kpi-completion" icon={Percent} label="Completion" value={`${k.completion_rate}%`} />
        <Kpi testid="kpi-avg-score" icon={Gauge} label="Avg Score" value={k.avg_score != null ? `${k.avg_score}%` : "—"} />
        <Kpi testid="kpi-open-actions" icon={ListChecks} label="Open Actions" value={k.open_actions} accent="text-amber-500" />
        <Kpi testid="kpi-overdue" icon={AlarmClock} label="Overdue" value={k.overdue_actions} accent="text-rose-600" />
      </div>

      {isCorp && (
        <div data-testid="dashboard-ai-card" className="bg-[#0F2E1B] text-white rounded-xl p-6 flex items-start gap-4 cursor-pointer hover:shadow-lg transition-shadow" onClick={() => navigate("/ai-insights")}>
          <div className="w-10 h-10 shrink-0 rounded-lg bg-emerald-400/20 flex items-center justify-center">
            <Sparkles className="w-5 h-5 text-emerald-300" strokeWidth={1.5} />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-bold tracking-tight">AI Insights</h3>
            {ai ? (
              <p className="text-sm text-white/70 mt-1 line-clamp-2">
                {ai.content.replace(/[#*_`\-]/g, " ").replace(/\s+/g, " ").trim().slice(0, 220)}…
              </p>
            ) : (
              <p className="text-sm text-white/70 mt-1">Let Claude analyze your audits to spot recurring issues across hotels and recommend standard improvements.</p>
            )}
          </div>
          <span className="shrink-0 flex items-center gap-1 text-xs font-semibold text-emerald-300">{ai ? "View analysis" : "Generate"} <ArrowRight className="w-3.5 h-3.5" /></span>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <div className="lg:col-span-7 bg-white border border-[#E7E5DF] rounded-xl p-6">
          <div className="flex items-center gap-2 mb-4">
            <TrendingUp className="w-4 h-4 text-[#14532D]" strokeWidth={1.5} />
            <h3 className="font-semibold text-stone-800">Score Trend</h3>
          </div>
          {data.trend.length === 0 ? (
            <p className="text-sm text-stone-400 py-12 text-center">No completed audits yet.</p>
          ) : (
            <ResponsiveContainer width="100%" height={240}>
              <AreaChart data={data.trend}>
                <defs>
                  <linearGradient id="g1" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#14532D" stopOpacity={0.25} />
                    <stop offset="100%" stopColor="#14532D" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#E7E5DF" />
                <XAxis dataKey="month" fontSize={11} stroke="#78766E" />
                <YAxis domain={[0, 100]} fontSize={11} stroke="#78766E" />
                <Tooltip />
                <Area type="monotone" dataKey="score" stroke="#14532D" strokeWidth={2} fill="url(#g1)" />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </div>
        <div className="lg:col-span-5 bg-white border border-[#E7E5DF] rounded-xl p-6">
          <h3 className="font-semibold text-stone-800 mb-1">Hotel Rankings</h3>
          <p className="text-[11px] text-stone-400 mb-4">{filters.template_id !== "all" ? "For the selected audit" : "Across all audits — pick an audit filter above for audit-wise ranking"}</p>
          {data.hotel_rankings.length === 0 ? (
            <p className="text-sm text-stone-400 py-12 text-center">No scores yet.</p>
          ) : (
            <div className="space-y-3" data-testid="hotel-rankings">
              {data.hotel_rankings.slice(0, 6).map((h, i) => (
                <div key={h.hotel} className="flex items-center gap-3">
                  <span className="w-6 text-xs font-bold text-stone-400 font-mono">#{i + 1}</span>
                  <div className="flex-1 min-w-0">
                    <div className="flex justify-between text-sm">
                      <span className="truncate text-stone-700">{h.hotel}</span>
                      <span className="font-semibold font-mono text-stone-900">{h.score}%</span>
                    </div>
                    <div className="h-1.5 bg-stone-100 rounded-full mt-1">
                      <div className={`h-1.5 rounded-full ${h.score >= 85 ? "bg-emerald-500" : h.score >= 70 ? "bg-amber-500" : "bg-rose-500"}`} style={{ width: `${h.score}%` }} />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <div className="lg:col-span-5 bg-white border border-[#E7E5DF] rounded-xl p-6">
          <h3 className="font-semibold text-stone-800 mb-4">Department Performance</h3>
          {data.department_performance.length === 0 ? (
            <p className="text-sm text-stone-400 py-8 text-center">No data yet.</p>
          ) : (
            <ResponsiveContainer width="100%" height={Math.max(160, data.department_performance.length * 36)}>
              <BarChart data={data.department_performance} layout="vertical" margin={{ left: 20 }}>
                <XAxis type="number" domain={[0, 100]} fontSize={11} stroke="#78766E" />
                <YAxis type="category" dataKey="department" width={90} fontSize={11} stroke="#57564F" />
                <Tooltip />
                <Bar dataKey="score" fill="#14532D" radius={[0, 4, 4, 0]} barSize={16} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
        <div className="lg:col-span-7 bg-white border border-[#E7E5DF] rounded-xl p-6">
          <h3 className="font-semibold text-stone-800 mb-4">Recent Audits</h3>
          <div className="overflow-x-auto">
          <table className="w-full min-w-[560px]">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-wider text-stone-500 border-b border-[#E7E5DF]">
                <th className="pb-2 pr-3">Audit</th>
                <th className="pb-2 pr-3">Hotel</th>
                <th className="pb-2 pr-3">Status</th>
                <th className="pb-2 text-right">Score</th>
              </tr>
            </thead>
            <tbody data-testid="recent-audits-table">
              {data.recent_audits.map((a) => (
                <tr key={a.id} className="border-b border-stone-100 last:border-0 hover:bg-stone-50/60 cursor-pointer" onClick={() => navigate(a.status === "completed" ? `/audits/${a.id}/report` : "/audits")}>
                  <td className="py-3 pr-3 text-sm text-stone-800">{a.template_name}</td>
                  <td className="py-3 pr-3 text-sm text-stone-500 truncate max-w-[180px]">{a.hotel_name}</td>
                  <td className="py-3 pr-3">
                    <span className={`inline-flex px-2 py-0.5 rounded-full text-[11px] font-medium border ${STATUS_STYLES[a.status]}`}>{a.status.replace("_", " ")}</span>
                  </td>
                  <td className="py-3 text-right font-mono text-sm font-semibold">{a.scores?.overall_score != null ? `${a.scores.overall_score}%` : "—"}</td>
                </tr>
              ))}
              {data.recent_audits.length === 0 && (
                <tr><td colSpan={4} className="py-8 text-center text-sm text-stone-400">No audits yet. Schedule one from the Audits page.</td></tr>
              )}
            </tbody>
          </table>
          </div>
          {data.repeat_issues.length > 0 && (
            <div className="mt-5 pt-4 border-t border-[#E7E5DF]">
              <div className="flex items-center gap-2 mb-2">
                <Repeat className="w-4 h-4 text-rose-600" strokeWidth={1.5} />
                <h4 className="text-sm font-semibold text-stone-800">Repeat Observations</h4>
              </div>
              {data.repeat_issues.map((r) => (
                <p key={r.issue} className="text-xs text-stone-500 py-1">• {r.issue} <span className="font-mono text-rose-600 font-semibold">×{r.count}</span></p>
              ))}
            </div>
          )}
        </div>
      </div>

      <DeptMatrix filters={filters} />

      {(data.actions_by_hotel || []).length > 0 && (
        <div className="bg-white border border-[#E7E5DF] rounded-xl p-6" data-testid="actions-by-hotel">
          <h3 className="font-semibold text-stone-800 mb-1">Corrective Actions Pending by Hotel</h3>
          <p className="text-[11px] text-stone-400 mb-4">Pending % = open action points as a share of total audited points. Click a hotel to deep-dive.</p>
          <div className="overflow-x-auto">
          <table className="w-full min-w-[680px]">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-wider text-stone-500 border-b border-[#E7E5DF]">
                <th className="pb-2 pr-3">Hotel</th>
                <th className="pb-2 pr-3 text-right">Open</th>
                <th className="pb-2 pr-3 text-right">Escalated</th>
                <th className="pb-2 pr-3 text-right">Closed</th>
                <th className="pb-2 pr-3 text-right">Pending Points</th>
                <th className="pb-2 pr-3 w-64">Pending %</th>
              </tr>
            </thead>
            <tbody>
              {data.actions_by_hotel.map((h) => (
                <tr key={h.hotel} data-testid={`actions-hotel-row-${h.hotel}`} className="border-b border-stone-100 last:border-0 hover:bg-stone-50/60 cursor-pointer" onClick={() => navigate(`/actions?hotel=${encodeURIComponent(h.hotel)}`)}>
                  <td className="py-3 pr-3 text-sm text-stone-800">{h.hotel}</td>
                  <td className="py-3 pr-3 text-right font-mono text-sm font-semibold text-amber-600">{h.open}</td>
                  <td className="py-3 pr-3 text-right font-mono text-sm font-bold text-rose-600">{h.escalated || 0}</td>
                  <td className="py-3 pr-3 text-right font-mono text-sm text-stone-500">{h.closed}</td>
                  <td className="py-3 pr-3 text-right font-mono text-sm text-stone-700">{h.pending_points}{h.total_points ? ` / ${h.total_points}` : ""}</td>
                  <td className="py-3 pr-3">
                    <div className="flex items-center gap-2">
                      <div className="flex-1 h-1.5 bg-stone-100 rounded-full">
                        <div className={`h-1.5 rounded-full ${(h.pending_pct || 0) > 15 ? "bg-rose-500" : (h.pending_pct || 0) > 5 ? "bg-amber-500" : "bg-emerald-500"}`} style={{ width: `${Math.min(h.pending_pct || 0, 100)}%` }} />
                      </div>
                      <span className="font-mono text-xs font-semibold text-stone-700 w-12 text-right">{h.pending_pct != null ? `${h.pending_pct}%` : "—"}</span>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </div>
      )}
    </div>
  );
}
